import axios from 'axios';
import { useState, useEffect } from 'react';
import useAutoSave from '../../hooks/useAutoSave';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, Trash2, DollarSign, Settings, FileText, Eye, Printer, Send, ZoomIn, ZoomOut, RotateCcw, X } from 'lucide-react';
import { toast } from 'sonner';
import { EditorRiche } from './EditorRiche';
import { SignatureCanvas } from './SignatureCanvas';
import { SectionItems } from './SectionItems';
import { SectionProduits } from './SectionProduits';
import { SectionEquipements } from './SectionEquipements';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { MoneyInput } from '@/lib/MoneyInput';

import { useEmailPreview, EmailPreviewFrame } from '../EmailPreview';
import { useFormShapeGuard } from '@/hooks/useFormShapeGuard';
import { urlFichier } from '@/lib/fichiers';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export function CreerSoumissionComplete({ isOpen, onClose, dossierId, onSuccess, soumissionToEdit, soumissionType = 'peinture' }) {
  const isLocation = soumissionType === 'location';
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [dossier, setDossier] = useState(null);
  const [client, setClient] = useState(null);
  const [infosEntreprise, setInfosEntreprise] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  const [showSignatureClient, setShowSignatureClient] = useState(false);
  const [showChoixTemplate, setShowChoixTemplate] = useState(false);
  const [showSauvegarderTemplate, setShowSauvegarderTemplate] = useState(false);
  const [showModifierTemplate, setShowModifierTemplate] = useState(false);
  const [templateType, setTemplateType] = useState('');
  const [templatesDisponibles, setTemplatesDisponibles] = useState([]);
  const [templateAModifier, setTemplateAModifier] = useState(null);
  const [documentsDisponibles, setDocumentsDisponibles] = useState([]);
  const [servicesCatalogue, setServicesCatalogue] = useState([]);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [signaturesActionnaires, setSignaturesActionnaires] = useState([]);
  // 🆕 2026-05-01 (Bug #1) — Signature historique (conservée depuis le doc) pour le cas
  // où l'actionnaire stocké a été modifié/supprimé depuis la création de la soumission.
  // On NE veut PAS écraser silencieusement par l'actionnaire #0 courant.
  const [signatureHistorique, setSignatureHistorique] = useState(null);  // {data, nom} | null
  const [showEnvoiModal, setShowEnvoiModal] = useState(false);
  const [envoiTemplates, setEnvoiTemplates] = useState([]);
  const [envoiData, setEnvoiData] = useState({ sujet: '', contenu: '', email_client: '', _preview: false });
  const [envoyee, setEnvoyee] = useState(false);
  const [sending, setSending] = useState(false);
  const [zoom, setZoom] = useState(100);
  const { previewHtml, loadingPreview, fetchPreview, clearPreview } = useEmailPreview();

  const initialFormData = {
    description_travaux: '',
    afficher_services: true,
    afficher_produits: false,
    afficher_equipements: false,
    items_services: [],
    items_produits: [],
    items_equipements: [],
    affichage: {
      service: true,
      qte: true,
      unite: true,
      total: true,
      sous_total: true,
      sous_total_detaille: false, // Afficher les lignes individuelles dans le sous-total
      taxes: true,
    },
    documents_selectionnes: [],
    notes: '',
    termes_conditions: '',
    date_soumission: new Date().toISOString().split('T')[0],
    numero_po: '',
    signature_client_data: '',
    signature_client_nom: '',
    depot_type: 'pourcentage',
    depot_valeur: 25,
    rabais_type: 'dollar',
    rabais_valeur: 0,
    afficher_rabais: true,
    adresse_livraison: '',
    ville_livraison: '',
    code_postal_livraison: '',
    livraison_meme_adresse: false,
  };

  const [formData, setFormData] = useState(initialFormData);

  // 🛡️ Filet de sécurité : voir /app/memory/PRD.md « Conventions de code »
  useFormShapeGuard('CreerSoumissionComplete', formData);

  const [newItem, setNewItem] = useState({
    description: '',
    quantite: 1,
    unite: 'unité',
    prix_unitaire: 0,
  });

  useEffect(() => {
    if (isOpen) {
      fetchAllData();
      // Mode édition: pré-remplir le formulaire
      if (soumissionToEdit) {
        setIsEditMode(true);
        setEnvoyee(
          soumissionToEdit.statut === 'soumission_envoyee' || 
          soumissionToEdit.statut === 'envoyee' || 
          soumissionToEdit.statut === 'acceptee' ||
          !!soumissionToEdit.date_envoi
        );
        
        // Séparer les items par type
        const items = soumissionToEdit.items || [];
        const itemsServices = items.filter(i => i.type === 'service' || !i.type);
        const itemsProduits = items.filter(i => i.type === 'produit');
        const itemsEquipements = items.filter(i => i.type === 'equipement');
        
        setFormData({
          // ✅ FIX 2026-04-27 : démarrer depuis initialFormData pour garantir
          // qu'aucun champ ne disparaît silencieusement (livraison, etc.)
          ...initialFormData,
          description_travaux: soumissionToEdit.description_travaux || '',
          afficher_services: itemsServices.length > 0 || items.length > 0,
          afficher_produits: itemsProduits.length > 0,
          afficher_equipements: itemsEquipements.length > 0,
          items_services: itemsServices.map(i => ({ description: i.description || '', montant: i.montant || 0 })),
          items_produits: itemsProduits,
          items_equipements: itemsEquipements,
          affichage: soumissionToEdit.affichage || initialFormData.affichage,
          documents_selectionnes: soumissionToEdit.documents_joints || [],
          notes: soumissionToEdit.notes || '',
          termes_conditions: soumissionToEdit.termes_conditions || '',
          date_soumission: soumissionToEdit.date_creation?.split('T')[0] || new Date().toISOString().split('T')[0],
          numero_po: soumissionToEdit.numero_po || '',
          signature_client_data: soumissionToEdit.signature_client_data || '',
          signature_client_nom: soumissionToEdit.signature_client_nom || '',
          depot_type: soumissionToEdit.depot_type || 'pourcentage',
          depot_valeur: soumissionToEdit.depot_valeur ?? soumissionToEdit.pourcentage_depot ?? 25,
          rabais_type: soumissionToEdit.rabais_type || 'dollar',
          rabais_valeur: soumissionToEdit.rabais_valeur || 0,
          afficher_rabais: soumissionToEdit.afficher_rabais !== false,
          // ✅ FIX 2026-04-27 : adresse de livraison également préservée à l'édition
          adresse_livraison: soumissionToEdit.adresse_livraison || '',
          ville_livraison: soumissionToEdit.ville_livraison || '',
          code_postal_livraison: soumissionToEdit.code_postal_livraison || '',
          livraison_meme_adresse: soumissionToEdit.livraison_meme_adresse || false,
        });
      } else {
        setIsEditMode(false);
        setEnvoyee(false);
        setSignatureHistorique(null);  // 🆕 Reset en création
        // Pour location: auto-activer équipements, désactiver services/produits
        if (isLocation) {
          setFormData({
            ...initialFormData,
            afficher_services: false,
            afficher_produits: false,
            afficher_equipements: true,
          });
        } else {
          setFormData(initialFormData);
        }
      }
    }
  }, [isOpen, soumissionToEdit, isLocation]);

  // 🆕 2026-05-01 (Bug #1) — Rehydrater signature_commercant_idx APRÈS chargement des
  // signaturesActionnaires ET de la soumission à éditer. Sans ce useEffect, le dropdown
  // et l'aperçu revenaient silencieusement à l'actionnaire #0, et un re-save écrasait
  // la signature historique avec celle du #0 courant (corruption silencieuse).
  useEffect(() => {
    if (!isOpen || !soumissionToEdit || signaturesActionnaires.length === 0) return;
    const savedNom = soumissionToEdit.signature_commercant_nom;
    const savedData = soumissionToEdit.signature_commercant_data;
    if (!savedNom && !savedData) {
      // Document sans signature commerçante sauvegardée : laisser l'idx à 0 (création initiale).
      setSignatureHistorique(null);
      return;
    }
    // Toujours conserver le couple historique pour fallback (signature + nom) :
    setSignatureHistorique({ data: savedData || '', nom: savedNom || '' });
    // Match strict nom + signature dans la liste actuelle.
    const matchIdx = signaturesActionnaires.findIndex(
      (s) => s && s.nom === savedNom && s.signature === savedData
    );
    // Si match → idx réel. Si pas de match → idx=-1 (l'UI affiche le badge "actionnaire modifié").
    setFormData((prev) => ({ ...prev, signature_commercant_idx: matchIdx >= 0 ? matchIdx : -1 }));
  }, [isOpen, soumissionToEdit, signaturesActionnaires]);

  const fetchAllData = async () => {
    setLoadingData(true);
    try {
      const [dossierRes, infosRes, docsRes, servicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/crm/dossiers/${dossierId}`),
        axios.get(`${API_URL}/api/entreprise/infos`),
        axios.get(`${API_URL}/api/messages-templates`),
        axios.get(`${API_URL}/api/services`),
      ]);

      let dossierData = null;
      if (dossierRes.status === 200) {
        const data = dossierRes.data;
        dossierData = data;
        setDossier(data);
        
        if (data.client_id) {
          const clientRes = await axios.get(`${API_URL}/api/crm/clients/${data.client_id}`);
          if (clientRes.status === 200) setClient(clientRes.data);
        }
        // Fetch representative phone from employees
        if (data.representant_id) {
          try {
            const empRes = await axios.get(`${API_URL}/api/employees/${data.representant_id}`);
            if (empRes.status === 200) {
              const emp = empRes.data;
              data.representant_telephone = emp.telephone || '';
              setDossier({ ...data });
            }
          } catch (e) { /* ignore */ }
        }
      }

      if (infosRes.status === 200) {
        const infos = infosRes.data;
        setInfosEntreprise(infos);
        // Load signatures: new array format or migrate old single signature
        let sigs = infos.signatures_actionnaires || [];
        if (sigs.length === 0 && infos.signature_commercant) {
          sigs = [{ nom: infos.nom_signataire_commercant || '', signature: infos.signature_commercant }];
        }
        setSignaturesActionnaires(sigs.filter(s => s && s.signature));
      }

      if (docsRes.status === 200) {
        // Legacy — ignored, documents now come from legal system
      }

      // Charger les services du catalogue
      if (servicesRes.status === 200) {
        try {
          const svcData = servicesRes.data;
          setServicesCatalogue((svcData || []).filter(s => s.is_active !== false));
        } catch (e) { console.error('Erreur chargement services:', e); }
      }

      // Charger les documents légaux selon le type de soumission
      if (dossierData) {
        const dept = isLocation ? 'LOCATION' : (dossierData.departement || 'PRQC');
        try {
          const legalRes = await axios.get(`${API_URL}/api/legal/documents-pour-soumission?departement=${encodeURIComponent(dept)}`);
          if (legalRes.status === 200) {
            const legalData = legalRes.data;
            const legalDocs = (legalData.documents || []).map(d => ({
              id: d.id || d.code,
              nom: d.nom,
              code: d.code,
              contenu: d.contenu,
              pdf_url: d.pdf_url || null,
              pdf_filename: d.pdf_filename || null,
              categorie: d.code,
              auto: true,
            }));
            setDocumentsDisponibles(legalDocs);
            // En mode création, auto-sélectionner les documents légaux
            if (!soumissionToEdit) {
              setFormData(prev => ({ ...prev, documents_selectionnes: legalDocs }));
            }
          }
        } catch (e) { console.error('Erreur chargement docs légaux:', e); }
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const ajouterItem = () => {
    if (!newItem.description) {
      toast.error('Veuillez remplir la description');
      return;
    }

    const montant = newItem.quantite * newItem.prix_unitaire;
    const item = { ...newItem, montant };
    
    // Ajouter dans items_services par défaut (sera changé avec composant SectionItems)
    setFormData(prev => ({
      ...prev,
      items_services: [...prev.items_services, item],
    }));

    setNewItem({
      description: '',
      quantite: 1,
      unite: 'unité',
      prix_unitaire: 0,
    });
  };

  const supprimerItem = (index) => {
    // Supprimer de items_services (sera changé avec composant)
    setFormData(prev => ({
      ...prev,
      items_services: prev.items_services.filter((_, i) => i !== index),
    }));
  };

  const calculerTotaux = () => {
    const totalServices = formData.items_services.reduce((sum, item) => sum + (item.exclure_sous_total ? 0 : (parseFloat(item.montant) || 0)), 0);
    const totalProduits = formData.items_produits.reduce((sum, item) => sum + (item.exclure_sous_total ? 0 : (parseFloat(item.montant) || 0)), 0);
    const totalEquipements = formData.items_equipements.reduce((sum, item) => sum + (item.exclure_sous_total ? 0 : (parseFloat(item.montant) || 0)), 0);
    const sousTotal = totalServices + totalProduits + totalEquipements;

    // Rabais avant taxes
    let rabais = 0;
    if (formData.rabais_valeur > 0) {
      if (formData.rabais_type === 'pourcentage') {
        rabais = sousTotal * (formData.rabais_valeur / 100);
      } else {
        rabais = formData.rabais_valeur;
      }
    }
    const sousTotalApresRabais = sousTotal - rabais;

    const tps = sousTotalApresRabais * 0.05;
    const tvq = sousTotalApresRabais * 0.09975;
    const total = sousTotalApresRabais + tps + tvq;
    
    // Dépôt en % ou en $
    let depot = 0;
    if (formData.depot_valeur > 0) {
      if (formData.depot_type === 'pourcentage') {
        depot = total * (formData.depot_valeur / 100);
      } else {
        depot = formData.depot_valeur;
      }
    }
    const balance = total - depot;

    return { totalServices, totalProduits, totalEquipements, sousTotal, rabais, sousTotalApresRabais, tps, tvq, total, depot, balance };
  };

  const handleSignatureClient = (signatureData, nomSignataire) => {
    setFormData(prev => ({
      ...prev,
      signature_client_data: signatureData,
      signature_client_nom: nomSignataire,
      date_signature_client: new Date().toLocaleDateString('fr-CA'),
    }));
    setShowSignatureClient(false);
  };


  const handleChoixTemplate = (type) => {
    setTemplateType(type);
    axios.get(`${API_URL}/api/messages-templates?categorie=${type === 'description' ? 'description_travaux' : type === 'notes' ? 'notes_soumission' : 'termes_conditions'}`).then(r => r.data)
      .then(data => {
        setTemplatesDisponibles(data.templates || []);
        setShowChoixTemplate(true);
      })
      .catch(err => console.error(err));
  };

  const handleSauvegarderTemplate = (type) => {
    setTemplateType(type);
    setShowSauvegarderTemplate(true);
  };

  const appliquerTemplate = (template) => {
    // Append content from multiple templates (separated by line break)
    if (templateType === 'description') {
      const existing = formData.description_travaux || '';
      const separator = existing && existing.trim() !== '' && existing !== '<p></p>' ? '\n\n' : '';
      setFormData({ ...formData, description_travaux: existing + separator + template.contenu });
    } else if (templateType === 'notes') {
      const existing = formData.notes || '';
      const separator = existing && existing.trim() !== '' && existing !== '<p></p>' ? '\n\n' : '';
      setFormData({ ...formData, notes: existing + separator + template.contenu });
    } else if (templateType === 'termes') {
      const existing = formData.termes_conditions || '';
      const separator = existing && existing.trim() !== '' && existing !== '<p></p>' ? '\n\n' : '';
      setFormData({ ...formData, termes_conditions: existing + separator + template.contenu });
    }
    // Don't close dialog - allow selecting more templates
    toast.success('Template ajouté! Vous pouvez en sélectionner un autre.');
  };

  const sauvegarderNouveauTemplate = async (nom) => {
    if (!nom || !nom.trim()) {
      toast.error('Veuillez entrer un nom');
      return;
    }

    let contenu = '';
    let categorie = 'autre';
    
    if (templateType === 'description') {
      contenu = formData.description_travaux;
      categorie = 'description_travaux';
    } else if (templateType === 'notes') {
      contenu = formData.notes;
      categorie = 'notes_soumission';
    } else if (templateType === 'termes') {
      contenu = formData.termes_conditions;
      categorie = 'termes_conditions';
    }

    if (!contenu || contenu.trim() === '' || contenu === '<p></p>') {
      toast.error('Le contenu est vide');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/messages-templates`, {
        nom: nom.trim(),
        categorie: categorie,
        sujet: nom.trim(),
        contenu: contenu,
        variables_disponibles: [],
        actif: true,
      });

      if (response.status !== 200) {
        const err = response.data || {};  // 🆕 Phase E2 — fix copy-paste axios
        throw new Error(err.detail || 'Erreur');
      }
      
      toast.success(`Template "${nom}" sauvegardé!`);
      setShowSauvegarderTemplate(false);
    } catch (error) {
      console.error(error);
      toast.error(error.message);
    }
  };


  const handleSubmit = async () => {
    // Combiner tous les items avec leur type
    const tousItems = isLocation
      ? formData.items_equipements.map(i => ({ ...i, type: 'equipement' }))
      : [
          ...formData.items_services.map(i => ({ ...i, type: 'service' })),
          ...formData.items_produits.map(i => ({ ...i, type: 'produit' })),
          ...formData.items_equipements.map(i => ({ ...i, type: 'equipement' })),
        ];
    
    if (tousItems.length === 0 && !isLocation) {
      // Permettre la sauvegarde sans items pour les soumissions peinture aussi
      // (l'utilisateur peut vouloir sauvegarder un brouillon)
    }

    setLoading(true);

    try {
      const totaux = calculerTotaux();
      
      const payload = {
        dossier_id: dossierId,
        client_id: dossier?.client_id || '',
        description_travaux: isLocation ? '' : formData.description_travaux,
        items: tousItems,
        sous_total: totaux.sousTotal,
        rabais_type: formData.rabais_type,
        rabais_valeur: formData.rabais_valeur,
        montant_rabais_calcule: totaux.rabais,
        afficher_rabais: formData.afficher_rabais,
        tps: totaux.tps,
        tvq: totaux.tvq,
        montant_final: totaux.total,
        depot_type: formData.depot_type,
        depot_valeur: formData.depot_valeur,
        montant_depot_calcule: totaux.depot,
        notes: isLocation ? '' : formData.notes,
        termes_conditions: isLocation ? '' : formData.termes_conditions,
        documents_joints: formData.documents_selectionnes,
        numero_po: formData.numero_po,
        signature_client_data: formData.signature_client_data,
        signature_client_nom: formData.signature_client_nom,
        // 🆕 2026-05-01 (Bug #1) — Signature commerçant :
        //   - idx >= 0 : utiliser la signature courante de l'actionnaire sélectionné
        //   - idx === -1 : actionnaire historique modifié/supprimé → CONSERVER la signature
        //     du document (signatureHistorique), NE PAS écraser avec #0 courant.
        signature_commercant_data: (
          formData.signature_commercant_idx === -1
            ? (signatureHistorique?.data || '')
            : (signaturesActionnaires[formData.signature_commercant_idx || 0]?.signature || '')
        ),
        signature_commercant_nom: (
          formData.signature_commercant_idx === -1
            ? (signatureHistorique?.nom || '')
            : (signaturesActionnaires[formData.signature_commercant_idx || 0]?.nom || '')
        ),
        type_soumission: soumissionType,
        adresse_livraison: formData.adresse_livraison || '',
        ville_livraison: formData.ville_livraison || '',
        code_postal_livraison: formData.code_postal_livraison || '',
        statut: isEditMode ? (soumissionToEdit?.statut || 'brouillon') : 'brouillon',
        // 🆕 2026-04-30 — Préférences d'affichage (incluant sous_total_detaille pour la vue client)
        affichage: formData.affichage || {},
      };

      let response;
      if (isEditMode && soumissionToEdit?.id) {
        // Mode édition: PUT
        response = await axios.put(`${API_URL}/api/crm/soumissions/${soumissionToEdit.id}`, payload);
      } else {
        // Mode création: POST
        response = await axios.post(`${API_URL}/api/crm/soumissions`, payload);
      }

      if (response.status !== 200) {
        const errorData = response.data || {};  // 🆕 Phase E2 — fix copy-paste axios
        throw new Error(errorData.detail || 'Erreur lors de la sauvegarde');
      }

      const data = response.data;
      const docLabel = isLocation ? 'Contrat de location' : 'Soumission';
      toast.success(isEditMode ? `${docLabel} mis(e) à jour!` : `${docLabel} ${data.numero_soumission} créé(e)!`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('soumission-print-area');
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    const clone = printContent.cloneNode(true);

    // 0. Ensure proper padding on the inner wrapper for print output
    const innerWrapper = clone.firstElementChild;
    if (innerWrapper) innerWrapper.style.padding = '24px';

    // 1. Remove internal-only elements (buttons, no-print classes, data-print-hide cards)
    clone.querySelectorAll('[data-print-hide], button, .no-print, [role="switch"]').forEach(el => el.remove());

    // 2. Remove TipTap editor toolbars
    clone.querySelectorAll('.tiptap-toolbar, [class*="bubble-menu"], .ProseMirror-menubar').forEach(el => el.remove());
    clone.querySelectorAll('.flex.items-center.gap-1.flex-wrap, .flex.gap-1.items-center').forEach(el => {
      if (el.querySelector('button') || el.closest('[data-print-hide]')) el.remove();
    });

    // 3. ✅ Remove all toggle labels (checkbox + label parent) — sinon "on" + texte du label se concatène
    clone.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      const checked = cb.checked;
      // Si c'est un toggle "Afficher rabais" décoché → on cachera la ligne entière plus bas
      cb.dataset.printChecked = checked ? '1' : '0';
      // Remonter au label parent et le supprimer
      const labelParent = cb.closest('label');
      if (labelParent) labelParent.remove();
      else cb.remove();
    });

    // 3b. ✅ Si "afficher_rabais" était décoché côté formulaire → cacher la ligne Rabais entière
    if (formData.afficher_rabais === false) {
      clone.querySelectorAll('tr.bg-amber-50').forEach(tr => tr.remove());
    }

    // 3c. ✅ Retirer les selects de configuration (rabais_type, depot_type) qui ne devraient
    //     pas apparaître à l'impression — la valeur est visible dans le suffixe ($ ou %).
    clone.querySelectorAll('select').forEach(sel => sel.remove());

    // 4. Replace remaining inputs/textareas with their display values
    clone.querySelectorAll('input, textarea').forEach(el => {
      const span = document.createElement('span');
      span.textContent = el.value || '';
      span.style.cssText = 'font: inherit;';
      el.replaceWith(span);
    });

    // 5. Remove empty content sections (empty rich text editors)
    clone.querySelectorAll('.ProseMirror').forEach(el => {
      if (!el.textContent.trim() && !el.querySelector('img')) {
        const card = el.closest('[data-slot="card"]');
        if (card) card.style.display = 'none';
      }
    });

    // 6. Remove ALL SVG icons from the print output (they don't render well in print)
    clone.querySelectorAll('svg').forEach(svg => {
      // Keep signature images but remove decorative icons
      if (!svg.closest('img')) svg.remove();
    });

    // 6b. ✅ Activer les éléments .print-only (cachés en édition, visibles en print)
    clone.querySelectorAll('.print-only').forEach(el => {
      el.style.display = '';
    });

    // 7. Handle client signature section - add signature line and date for print
    const clientSignatureSection = clone.querySelector('[data-testid="client-signature-section"]');
    if (clientSignatureSection) {
      const hasSignature = clientSignatureSection.querySelector('img');
      if (!hasSignature) {
        // No signature yet - add lines for signature and date
        const sigArea = clientSignatureSection.querySelector('[data-testid="sign-client-btn"]')?.closest('div');
        if (sigArea) {
          sigArea.innerHTML = `
            <div style="margin-top: 20px;">
              <div style="border-bottom: 1px solid #000; width: 280px; margin-bottom: 5px;"></div>
              <p style="font-size: 10px; color: #333; margin: 0;">Signature du client</p>
            </div>
            <div style="margin-top: 15px;">
              <div style="border-bottom: 1px solid #000; width: 180px; margin-bottom: 5px;"></div>
              <p style="font-size: 10px; color: #333; margin: 0;">Date</p>
            </div>
          `;
        }
      } else {
        // Signature exists - add date line below it if not already shown
        const dateSection = document.createElement('div');
        dateSection.innerHTML = `
          <div style="margin-top: 15px;">
            <div style="border-bottom: 1px solid #000; width: 180px; margin-bottom: 5px;"></div>
            <p style="font-size: 10px; color: #333; margin: 0;">Date</p>
          </div>
        `;
        clientSignatureSection.appendChild(dateSection);
      }
    }

    // Also handle cases where data-testid wasn't found - fallback to label search
    const allLabels = clone.querySelectorAll('label');
    allLabels.forEach(label => {
      if (label.textContent.includes('Signature Client')) {
        const container = label.closest('div');
        if (container && !container.querySelector('img') && !container.querySelector('[style*="border-bottom"]')) {
          const sigBtn = container.querySelector('button');
          if (sigBtn) {
            sigBtn.outerHTML = `
              <div style="margin-top: 20px;">
                <div style="border-bottom: 1px solid #000; width: 280px; margin-bottom: 5px;"></div>
                <p style="font-size: 10px; color: #333; margin: 0;">Signature du client</p>
              </div>
              <div style="margin-top: 15px;">
                <div style="border-bottom: 1px solid #000; width: 180px; margin-bottom: 5px;"></div>
                <p style="font-size: 10px; color: #333; margin: 0;">Date</p>
              </div>
            `;
          }
        }
      }
    });

    const html = `<!DOCTYPE html><html><head>
      <title>${isLocation ? 'Contrat de Location' : 'Soumission'}</title>
      <style>
        @page { size: letter; margin: 0.65in 0.7in; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #111; line-height: 1.5; }
        
        /* ====== CARDS — only target top-level Card wrappers ====== */
        [data-slot="card"] {
          border: 1px solid #e5e7eb;
          box-shadow: none;
          background: white;
          border-radius: 8px;
          margin-bottom: 16px;
          overflow: hidden;
        }
        [data-slot="card-header"] {
          padding: 12px 16px;
          border-bottom: 1px solid #e5e7eb;
        }
        [data-slot="card-content"] {
          padding: 16px;
        }
        [data-slot="card-title"] {
          font-size: 16px;
          font-weight: 700;
          color: #111827;
          margin: 0;
        }
        
        /* Header section */
        h1 { font-size: 24px; text-align: center; margin: 16px 0; font-weight: 700; color: #111827; }
        h2 { font-size: 18px; font-weight: bold; margin-bottom: 10px; color: #111827; }
        h3, h4 { font-size: 14px; font-weight: 600; color: #111827; }
        
        /* Grid layouts */
        .grid { display: grid; gap: 12px; }
        .grid-cols-12 { grid-template-columns: repeat(12, 1fr); }
        .grid-cols-3 { grid-template-columns: repeat(3, 1fr); }
        .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
        .col-span-3 { grid-column: span 3; }
        .col-span-6 { grid-column: span 6; }
        
        /* Tables */
        table { width: 100%; border-collapse: collapse; margin: 6px 0; }
        th { background: #f9fafb !important; font-weight: 600; font-size: 10px; padding: 8px 10px; border: 1px solid #e5e7eb; text-transform: uppercase; color: #374151; }
        td { padding: 6px 10px; border: 1px solid #e5e7eb; font-size: 11px; }
        tr:nth-child(even) td { background: #f9fafb; }
        /* Totaux table — no cell borders */
        .max-w-md table td { border: none; }
        .max-w-md table tr { border-bottom: 1px solid #e5e7eb; }
        
        /* Typography */
        .font-bold { font-weight: 700; }
        .font-semibold { font-weight: 600; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .text-left { text-align: left; }
        .text-sm { font-size: 11px; }
        .text-xs { font-size: 10px; }
        .text-base { font-size: 12px; }
        .text-lg { font-size: 14px; }
        .text-2xl { font-size: 18px; }
        .text-4xl { font-size: 24px; }
        
        /* Colors */
        .text-green-700, .text-green-600 { color: #15803d; }
        .text-blue-600, .text-blue-700 { color: #2563eb; }
        .text-amber-700 { color: #b45309; }
        .text-gray-500, .text-gray-600 { color: #6b7280; }
        .text-gray-700, .text-gray-800, .text-gray-900 { color: #111827; }
        
        /* Spacing */
        .mt-1 { margin-top: 4px; } .mt-2 { margin-top: 8px; } .mt-3 { margin-top: 12px; } .mt-4 { margin-top: 16px; }
        .mb-2 { margin-bottom: 8px; } .mb-3 { margin-bottom: 12px; } .mb-4 { margin-bottom: 16px; }
        .p-2 { padding: 8px; } .p-3 { padding: 12px; } .p-4 { padding: 16px; } .p-6 { padding: 24px; }
        .pt-0 { padding-top: 0; } .pt-2 { padding-top: 8px; } .pt-4 { padding-top: 16px; } .pt-6 { padding-top: 24px; }
        .pl-3 { padding-left: 12px; }
        .px-3 { padding-left: 12px; padding-right: 12px; } .px-4 { padding-left: 16px; padding-right: 16px; }
        .py-2 { padding-top: 8px; padding-bottom: 8px; }
        .border { border: 1px solid #e5e7eb; }
        .border-t { border-top: 1px solid #e5e7eb; }
        .border-b { border-bottom: 1px solid #e5e7eb; }
        .border-b-2 { border-bottom: 2px solid #000; }
        .rounded { border-radius: 6px; }
        .rounded-lg { border-radius: 8px; }
        .space-y-1 > * + * { margin-top: 4px; }
        .space-y-2 > * + * { margin-top: 8px; }
        .space-y-3 > * + * { margin-top: 12px; }
        .space-y-4 > * + * { margin-top: 16px; }
        .space-y-6 > * + * { margin-top: 24px; }
        .gap-2 { gap: 8px; } .gap-3 { gap: 12px; } .gap-4 { gap: 16px; } .gap-6 { gap: 24px; } .gap-8 { gap: 32px; }
        
        /* Flex */
        .flex { display: flex; } .flex-1 { flex: 1; } .flex-wrap { flex-wrap: wrap; }
        .items-center { align-items: center; } .items-start { align-items: flex-start; }
        .justify-between { justify-content: space-between; } .justify-center { justify-content: center; } .justify-end { justify-content: flex-end; }
        
        /* Width helpers */
        .w-full { width: 100%; }
        .w-48 { width: 12rem; }
        .w-16 { width: 4rem; }
        .max-w-md { max-width: 28rem; margin-left: auto; }
        .ml-auto { margin-left: auto; }
        
        /* Images */
        img { max-width: 100%; height: auto; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .object-contain { object-fit: contain; }
        
        /* Signature area */
        .h-24 { height: 70px; }
        .border.rounded.h-24 { height: 70px; border: 1px solid #d1d5db; border-radius: 6px; background: white; }
        
        /* Partners logos */
        .h-14 { height: 42px; }
        .w-auto { width: auto; }
        
        /* Backgrounds */
        .bg-gray-50, .bg-gray-100 { background: #f9fafb !important; }
        .bg-gray-200 { background: #e5e7eb !important; }
        .bg-blue-50 { background: #eff6ff !important; }
        .bg-amber-50 { background: #fffbeb !important; }
        .bg-white { background: white; }
        
        /* Rich text (TipTap editor content) */
        .ProseMirror, .prose { font-size: 12px; line-height: 1.6; }
        .ProseMirror p, .prose p { margin: 4px 0; }
        .ProseMirror h1 { font-size: 20px; font-weight: 700; margin: 12px 0 6px; }
        .ProseMirror h2 { font-size: 17px; font-weight: 700; margin: 10px 0 4px; }
        .ProseMirror h3 { font-size: 15px; font-weight: 600; margin: 8px 0 4px; }
        .ProseMirror strong { font-weight: 700; }
        .ProseMirror ul { padding-left: 20px; list-style: disc; }
        .ProseMirror ol { padding-left: 20px; list-style: decimal; }
        .ProseMirror li { margin: 2px 0; }
        
        /* Documents joints content */
        .doc-contenu-print { font-size: 11px; line-height: 1.5; color: #374151; text-align: justify; white-space: pre-wrap; }
        
        /* Italic */
        .italic { font-style: italic; }
        
        /* Separators */
        .my-8 { margin: 16px 0; }
        
        /* Print-specific */
        @media print {
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          body { padding: 0; margin: 0; }
        }
      </style>
    </head><body>${clone.innerHTML}</body></html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 600);
  };

  const handleOpenEnvoi = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/messages-templates?categorie=soumission`);
      if (res.status === 200) {
        const data = res.data;
        const templates = data.templates || [];
        setEnvoiTemplates(templates);
        
        // Set initial email from client
        const initialData = { sujet: '', contenu: '', email_client: client?.email || '' };
        
        // Auto-select first template if available
        if (templates.length > 0) {
          const firstTemplate = templates[0];
          let contenu = firstTemplate.contenu || '';
          let sujet = firstTemplate.sujet || '';
          
          // Replace all variables
          const vars = getTemplateVariables();
          Object.entries(vars).forEach(([k, v]) => {
            contenu = contenu.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
            sujet = sujet.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
          });
          initialData.sujet = sujet;
          initialData.contenu = contenu;
        }
        
        setEnvoiData(initialData);
      }
    } catch (e) { console.error(e); }
    setShowEnvoiModal(true);
  };

  // Fonction pour construire les variables de remplacement
  const [lienPublicServeur, setLienPublicServeur] = useState('');
  useEffect(() => {
    const id = soumissionToEdit?.id;
    if (!id || !soumissionToEdit?.token_public) { setLienPublicServeur(''); return; }
    let actif = true;
    axios.get(`${API_URL}/api/crm/soumissions/${id}/lien-public`).then(r => { if (actif) setLienPublicServeur(r.data?.url || ''); }).catch(() => {});
    return () => { actif = false; };
  }, [soumissionToEdit?.id, soumissionToEdit?.token_public]);

  const getTemplateVariables = () => {
    // Utiliser le token de la soumission en mode édition
    const tokenPublic = soumissionToEdit?.token_public || '';
    // Le lien pointe vers la page React /soumission/{token} qui affiche la soumission complète
    // 🚨 URL construite côté serveur (url_app) — plus jamais depuis le domaine du navigateur de l'expéditeur
    const lienSoumissionUrl = lienPublicServeur || '';
    
    // Lien cliquable "Cliquez ici" pour la soumission
    const lienSoumission = tokenPublic 
      ? `<a href="${lienSoumissionUrl}" style="color: #2563eb; text-decoration: underline; font-weight: bold;">Cliquez ici</a>`
      : '(Enregistrez la soumission pour générer le lien)';
    
    // Lien cliquable "Avis Google ⭐"
    const avisGoogle = infosEntreprise?.lien_avis_google 
      ? `<a href="${infosEntreprise.lien_avis_google}" style="color: #2563eb; text-decoration: underline; font-weight: bold;">Avis Google ⭐</a>`
      : 'Avis Google ⭐';
    
    return {
      // Client info
      client_nom: client?.nom || '',
      client_prenom: client?.prenom || '',
      client_email: client?.email || '',
      client_telephone: client?.telephone || '',
      
      // Dossier/Soumission info
      numero_soumission: soumissionToEdit?.numero_soumission || dossier?.numero_dossier || '',
      numero_dossier: dossier?.numero_dossier || '',
      description_projet: dossier?.description || '',
      
      // Montants
      montant: totaux.total.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      montant_total: totaux.total.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      montant_depot: totaux.depot.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      depot: totaux.depot.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      
      // Entreprise info
      nom_entreprise: infosEntreprise?.nom_entreprise || 'Peinture Résidentielle QC',
      entreprise: infosEntreprise?.nom_entreprise || 'Peinture Résidentielle QC',
      telephone_entreprise: infosEntreprise?.telephone || '',
      email_entreprise: infosEntreprise?.email || '',
      adresse_entreprise: infosEntreprise?.adresse || '',
      numero_rbq: infosEntreprise?.numero_rbq ? `RBQ : ${infosEntreprise.numero_rbq}` : '',
      
      // Représentant
      representant: dossier?.representant_nom || infosEntreprise?.nom_signataire_commercant || 'L\'équipe',
      nom_representant: dossier?.representant_nom || infosEntreprise?.nom_signataire_commercant || 'L\'équipe',
      telephone_representant: dossier?.representant_telephone || '',
      
      // Liens cliquables
      lien_soumission: lienSoumission,
      avis_google: avisGoogle,
      
      // Logo email — URL HTTPS publique (Gmail bloque les images base64)
      logo_email: infosEntreprise?.logo_email
        ? `<div style="text-align:left;margin-top:2px;margin-bottom:0;padding:0;"><img src="${API_URL}${infosEntreprise.logo_email}" alt="${infosEntreprise?.nom_entreprise || ''}" style="display:inline-block;vertical-align:top;width:180px;margin:0;padding:0;" /></div>`
        : '',
    };
  };

  const handleSelectEnvoiTemplate = (template) => {
    let contenu = template.contenu || '';
    let sujet = template.sujet || '';
    
    // Replace all variables
    const vars = getTemplateVariables();
    Object.entries(vars).forEach(([k, v]) => {
      contenu = contenu.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
      sujet = sujet.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
    });
    setEnvoiData(prev => ({ ...prev, sujet, contenu }));
  };

  const handleSendEmail = async () => {
    if (!envoiData.email_client) { toast.error('Email du client requis'); return; }
    if (!envoiData.sujet || !envoiData.contenu) { toast.error('Sujet et contenu requis'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/crm/envoyer-soumission`, {
          dossier_id: dossierId,
          soumission_id: soumissionToEdit?.id || '',
          email_destinataire: envoiData.email_client,
          sujet: envoiData.sujet,
          contenu: envoiData.contenu,
        });
      if (res.status !== 200) {
        const errorData = res.data || {};  // 🆕 Phase E2 — fix copy-paste axios
        throw new Error(errorData.detail || 'Erreur envoi');
      }
      toast.success('Soumission envoyée par email! Le statut du projet a été mis à jour.');
      setEnvoyee(true);
      setShowEnvoiModal(false);
      // Refresh the parent data to reflect the new status
      if (onSuccess) onSuccess();
    } catch (e) {
      console.error('Erreur envoi:', e);
      toast.error(`Erreur lors de l'envoi: ${e.message}`);
    } finally { setSending(false); }
  };

  const totaux = calculerTotaux();

  // 🆕 [Phase 43 #6] Auto-save silencieux (debounce 2s) — uniquement en mode édition.
  // ⚠️ Hook DOIT être appelé AVANT l'early return pour respecter Rules of Hooks.
  const autoSaveFn = async () => {
    if (!isEditMode || !soumissionToEdit?.id) return;
    const tousItems = isLocation
      ? formData.items_equipements.map(i => ({ ...i, type: 'equipement' }))
      : [
          ...formData.items_services.map(i => ({ ...i, type: 'service' })),
          ...formData.items_produits.map(i => ({ ...i, type: 'produit' })),
          ...formData.items_equipements.map(i => ({ ...i, type: 'equipement' })),
        ];
    const tTotaux = calculerTotaux();
    const payload = {
      dossier_id: dossierId,
      client_id: dossier?.client_id || '',
      description_travaux: isLocation ? '' : formData.description_travaux,
      items: tousItems,
      sous_total: tTotaux.sousTotal,
      rabais_type: formData.rabais_type,
      rabais_valeur: formData.rabais_valeur,
      montant_rabais_calcule: tTotaux.rabais,
      afficher_rabais: formData.afficher_rabais,
      tps: tTotaux.tps,
      tvq: tTotaux.tvq,
      montant_final: tTotaux.total,
      depot_type: formData.depot_type,
      depot_valeur: formData.depot_valeur,
      montant_depot_calcule: tTotaux.depot,
      notes: isLocation ? '' : formData.notes,
      termes_conditions: isLocation ? '' : formData.termes_conditions,
      documents_joints: formData.documents_selectionnes,
      numero_po: formData.numero_po,
      type_soumission: soumissionType,
      adresse_livraison: formData.adresse_livraison || '',
      ville_livraison: formData.ville_livraison || '',
      code_postal_livraison: formData.code_postal_livraison || '',
      statut: soumissionToEdit?.statut || 'brouillon',
      affichage: formData.affichage || {},
    };
    await axios.put(`${API_URL}/api/crm/soumissions/${soumissionToEdit.id}`, payload);
  };
  const { status: autoSaveStatus, lastSavedAt } = useAutoSave(
    formData,
    autoSaveFn,
    { debounceMs: 2000, enabled: !!(isEditMode && soumissionToEdit?.id) }
  );

  if (loadingData) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-[95vw] max-h-[95vh]">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // 🆕 [Phase 43 #6] (placeholder — vrai hook est avant l'early return ci-dessus)
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] flex flex-col overflow-hidden p-0 [&>button.absolute]:hidden">
        {/* Top bar - fixed */}
        <div className="flex justify-between items-center px-6 py-3 border-b bg-white shrink-0">
          <DialogHeader className="p-0 m-0">
            <DialogTitle>{isLocation ? 'Créer un Contrat de Location' : 'Créer une Soumission'}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-4 no-print">
            {/* 🆕 [Phase 43 #6] Indicateur auto-save discret */}
            {isEditMode && soumissionToEdit?.id && (
              <span
                className={`text-[11px] flex items-center gap-1 ${
                  autoSaveStatus === 'saving' ? 'text-blue-600' :
                  autoSaveStatus === 'saved' ? 'text-emerald-600' :
                  autoSaveStatus === 'pending' ? 'text-amber-600' :
                  autoSaveStatus === 'error' ? 'text-red-600' : 'text-slate-400'
                }`}
                data-testid="autosave-indicator"
                title="Sauvegarde automatique active (debounce 2 s)"
              >
                {autoSaveStatus === 'saving' && <>💾 Sauvegarde…</>}
                {autoSaveStatus === 'pending' && <>⏳ En attente…</>}
                {autoSaveStatus === 'saved' && lastSavedAt && (
                  <>💾 Sauvegardé à {lastSavedAt.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}</>
                )}
                {autoSaveStatus === 'error' && <>⚠️ Échec auto-save (réessai au prochain changement)</>}
                {autoSaveStatus === 'idle' && <>💾 Auto-save activé</>}
              </span>
            )}
            {/* Zoom controls */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg px-1 py-1" data-testid="zoom-controls">
              <button
                onClick={() => setZoom(z => Math.max(40, z - 10))}
                className="w-8 h-8 rounded-md hover:bg-gray-200 flex items-center justify-center transition-colors disabled:opacity-30"
                title="Éloigner"
                data-testid="zoom-out-btn"
                disabled={zoom <= 40}
              >
                <ZoomOut className="w-4 h-4 text-gray-600" />
              </button>
              <span className="text-xs font-medium text-gray-600 w-10 text-center select-none" data-testid="zoom-level">{zoom}%</span>
              <button
                onClick={() => setZoom(z => Math.min(100, z + 10))}
                className="w-8 h-8 rounded-md hover:bg-gray-200 flex items-center justify-center transition-colors disabled:opacity-30"
                title="Rapprocher"
                data-testid="zoom-in-btn"
                disabled={zoom >= 100}
              >
                <ZoomIn className="w-4 h-4 text-gray-600" />
              </button>
              {zoom !== 100 && (
                <button
                  onClick={() => setZoom(100)}
                  className="w-8 h-8 rounded-md hover:bg-gray-200 flex items-center justify-center transition-colors"
                  title="Réinitialiser"
                  data-testid="zoom-reset-btn"
                >
                  <RotateCcw className="w-3.5 h-3.5 text-gray-600" />
                </button>
              )}
            </div>
            {/* Print & Send */}
            <div className="flex gap-2">
              <button
                onClick={handlePrint}
                className="w-10 h-10 rounded-lg bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center transition-colors shadow-sm"
                title="Imprimer / Sauvegarder en PDF"
                data-testid="btn-print"
              >
                <Printer className="w-5 h-5" />
              </button>
              <button
                onClick={handleOpenEnvoi}
                className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center transition-colors shadow-sm ${envoyee ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700'}`}
                title={envoyee ? 'Soumission envoyée' : 'Envoyer la soumission par email'}
                data-testid="btn-send"
              >
                <Send className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="w-10 h-10 rounded-lg border-2 border-gray-300 bg-white text-gray-500 hover:border-red-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors shadow-sm"
                title="Fermer"
                data-testid="btn-close-soumission"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Document viewer area - scrollable gray background */}
        <div className="flex-1 overflow-auto" style={{ background: '#e5e7eb' }}>
          <div style={{ padding: zoom < 100 ? '24px' : '0px', display: 'flex', justifyContent: 'center' }}>
            {/* The "sheet of paper" */}
            <div
              style={{
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top center',
                transition: 'transform 0.2s ease',
                width: '100%',
                maxWidth: zoom < 100 ? `${95 / (zoom / 100)}vw` : 'none',
              }}
            >
              <div
                id="soumission-print-area"
                style={{
                  background: 'white',
                  boxShadow: zoom < 100 ? '0 4px 24px rgba(0,0,0,0.15)' : 'none',
                  borderRadius: zoom < 100 ? '4px' : '0',
                }}
              >
                <div className="space-y-2" style={{ padding: zoom < 100 ? '32px' : '0' }}>
          {/* EN-TÊTE PROPRE: Logo GAUCHE | Infos CENTRE | Dossier DROITE */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-12 gap-8 items-start">
                {/* GAUCHE: LOGO (3 colonnes) */}
                <div className="col-span-3 flex items-start">
                  {infosEntreprise?.logo_principal ? (
                    <img
                      src={`${API_URL}${infosEntreprise.logo_principal}`}
                      alt="Logo"
                      className="w-full h-auto object-contain"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.parentElement.innerHTML = '<div class="w-full h-40 bg-gray-200 flex items-center justify-center rounded"><span class="text-gray-500">Logo indisponible</span></div>';
                      }}
                    />
                  ) : (
                    <div className="w-full h-40 bg-gray-200 flex items-center justify-center rounded">
                      <span className="text-gray-500">Aucun logo</span>
                    </div>
                  )}
                </div>

                {/* CENTRE: INFOS ENTREPRISE (6 colonnes) */}
                <div className="col-span-6">
                  <h2 className="font-bold text-2xl mb-4 text-center">{infosEntreprise?.nom_entreprise}</h2>
                  <div className="text-sm text-gray-900 space-y-1 text-center">
                    <p>{infosEntreprise?.adresse}</p>
                    <p>{infosEntreprise?.ville} {infosEntreprise?.code_postal}</p>
                    <p>{infosEntreprise?.province}, Canada</p>
                    <p className="mt-3 font-semibold">{infosEntreprise?.telephone}</p>
                    <p>{infosEntreprise?.email}</p>
                    {infosEntreprise?.site_web && <p>{infosEntreprise.site_web}</p>}
                  </div>
                </div>

                {/* DROITE: INFOS DOSSIER (3 colonnes) */}
                <div className="col-span-3 text-sm">
                  <div className="space-y-2">
                    <p className="font-bold text-base">Dossier: {dossier?.numero_dossier}</p>
                    {dossier?.representant_nom && (
                      <p><span className="font-semibold">Créé par:</span> {dossier.representant_nom}{dossier.representant_telephone ? ` — ${dossier.representant_telephone}` : ''}</p>
                    )}
                    <p><span className="font-semibold">Date:</span> {new Date(dossier?.date_creation || new Date()).toLocaleDateString('fr-CA')}</p>
                    <p><span className="font-semibold">Transmis:</span> {new Date().toLocaleDateString('fr-CA')}</p>
                    
                    <div className="mt-4 pt-4 border-t space-y-1 text-xs">
                      {infosEntreprise?.numero_tps && <p><strong>TPS:</strong> {infosEntreprise.numero_tps}</p>}
                      {infosEntreprise?.numero_tvq && <p><strong>TVQ:</strong> {infosEntreprise.numero_tvq}</p>}
                      {infosEntreprise?.numero_rbq && <p><strong>RBQ:</strong> {infosEntreprise.numero_rbq}</p>}
                      {infosEntreprise?.numero_neq && <p><strong>NEQ:</strong> {infosEntreprise.numero_neq}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* TITRE */}
              <h1 className="text-4xl font-bold text-center my-8">{isLocation ? 'CONTRAT DE LOCATION' : 'SOUMISSION'}</h1>

              {/* CLIENT + ADRESSES */}
              <div className={`grid ${isLocation ? 'grid-cols-2' : 'grid-cols-3'} gap-8 text-sm`}>
                <div>
                  <p className="font-bold text-base mb-3">Client :</p>
                  <p className="font-semibold text-base mb-2">{client?.nom}</p>
                  <p>Contact: {client?.nom}</p>
                  <p>Téléphone: {client?.telephone}</p>
                  <p>Courriel: {client?.email || 'N/A'}</p>
                </div>

                <div>
                  <p className="font-bold text-base mb-3">Adresse du client :</p>
                  <div className="text-gray-900">
                    <p>{client?.adresse_client}</p>
                    <p>{client?.ville_client} {client?.code_postal_client}</p>
                    <p>{client?.ville_client}, Canada</p>
                  </div>
                </div>

                <div>
                  <p className="font-bold text-base mb-3">Adresse des travaux :</p>
                  <div className="text-gray-900">
                    <p>{dossier?.adresse_travaux}</p>
                    <p>{dossier?.ville_travaux} {dossier?.code_postal_travaux}</p>
                    <p>{dossier?.ville_travaux}, Canada</p>
                  </div>
                </div>

                {isLocation && (
                <div>
                  <p className="font-bold text-base mb-3">Adresse de livraison :</p>
                  <div className="text-gray-900">
                    <p>{formData.adresse_livraison || <span className="text-gray-400 italic">Non spécifiée</span>}</p>
                    <p>{formData.ville_livraison} {formData.code_postal_livraison}</p>
                  </div>
                </div>
                )}
              </div>

              {/* FORMULAIRE ADRESSE DE LIVRAISON - pour contrats de location (non imprimé) */}
              {isLocation && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-print-hide="true">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-sm text-amber-800">Modifier l'adresse de livraison :</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="btn-meme-adresse"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          adresse_livraison: dossier?.adresse_travaux || '',
                          ville_livraison: dossier?.ville_travaux || '',
                          code_postal_livraison: dossier?.code_postal_travaux || '',
                          livraison_meme_adresse: true,
                        }));
                      }}
                      className="text-xs"
                    >
                      Utiliser la même adresse des travaux
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      data-testid="input-adresse-livraison"
                      value={formData.adresse_livraison}
                      onChange={(e) => setFormData({ ...formData, adresse_livraison: e.target.value, livraison_meme_adresse: false })}
                      placeholder="123 rue Exemple"
                      className="bg-white"
                    />
                    <Input
                      data-testid="input-ville-livraison"
                      value={formData.ville_livraison}
                      onChange={(e) => setFormData({ ...formData, ville_livraison: e.target.value, livraison_meme_adresse: false })}
                      placeholder="Ville"
                      className="bg-white"
                    />
                    <Input
                      data-testid="input-cp-livraison"
                      value={formData.code_postal_livraison}
                      onChange={(e) => setFormData({ ...formData, code_postal_livraison: e.target.value, livraison_meme_adresse: false })}
                      placeholder="G1A 1A1"
                      className="bg-white"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* DESCRIPTION - masqué pour location */}
          {!isLocation && (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-bold">Description des Travaux</CardTitle>
            </CardHeader>
            <CardContent>
              <EditorRiche
                value={formData.description_travaux}
                onChange={(value) => setFormData({ ...formData, description_travaux: value })}
                onChoixTemplate={() => handleChoixTemplate('description')}
                onSauvegarderTemplate={() => handleSauvegarderTemplate('description', formData.description_travaux)}
                placeholder="Décrivez les travaux..."
              />
            </CardContent>
          </Card>
          )}

          {/* 2. NOTES - masqué pour location */}
          {!isLocation && (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-bold">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <EditorRiche
                value={formData.notes}
                onChange={(value) => setFormData({ ...formData, notes: value })}
                onChoixTemplate={() => handleChoixTemplate('notes')}
                onSauvegarderTemplate={() => handleSauvegarderTemplate('notes')}
                placeholder="Notes additionnelles..."
              />
            </CardContent>
          </Card>
          )}

          {/* SÉLECTION SECTIONS - masqué pour location */}
          {!isLocation && (
          <Card className="bg-blue-50" data-print-hide="true">
            <CardHeader>
              <CardTitle>Sections à Afficher</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="services"
                    checked={formData.afficher_services}
                    onCheckedChange={(c) => setFormData({ ...formData, afficher_services: c })}
                  />
                  <Label htmlFor="services" className="font-medium cursor-pointer">Services</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="produits"
                    checked={formData.afficher_produits}
                    onCheckedChange={(c) => setFormData({ ...formData, afficher_produits: c })}
                  />
                  <Label htmlFor="produits" className="font-medium cursor-pointer">Produits</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="equipements"
                    checked={formData.afficher_equipements}
                    onCheckedChange={(c) => setFormData({ ...formData, afficher_equipements: c })}
                  />
                  <Label htmlFor="equipements" className="font-medium cursor-pointer">Équipements</Label>
                </div>
              </div>
            </CardContent>
          </Card>
          )}

          {/* SECTION SERVICES - masqué pour location */}
          {!isLocation && formData.afficher_services && (
            <>
            {servicesCatalogue.length > 0 && (
              <div className="flex items-center gap-2 px-1" data-print-hide="true">
                <select
                  className="border rounded-md px-3 py-2 text-sm bg-white flex-1"
                  defaultValue=""
                  onChange={(e) => {
                    const svc = servicesCatalogue.find(s => s.id === e.target.value);
                    if (svc) {
                      setFormData(prev => ({
                        ...prev,
                        items_services: [...prev.items_services, { description: svc.nom, montant: svc.taux_horaire || 0 }]
                      }));
                      e.target.value = '';
                    }
                  }}
                  data-testid="service-catalogue-dropdown"
                >
                  <option value="">Ajouter un service du catalogue...</option>
                  {servicesCatalogue.map(svc => (
                    <option key={svc.id} value={svc.id}>{svc.nom} — {svc.taux_horaire}$/h</option>
                  ))}
                </select>
              </div>
            )}
            <SectionItems
              titre="Services"
              items={formData.items_services}
              onModifierItem={(idx, champ, val) => {
                const updated = formData.items_services.map((item, i) => 
                  i === idx ? { ...item, [champ]: champ === 'montant' ? (parseFloat(val) || 0) : val } : item
                );
                setFormData({ ...formData, items_services: updated });
              }}
              onAjouterLigne={() => {
                setFormData({ ...formData, items_services: [...formData.items_services, { description: '', montant: 0 }] });
              }}
              onSupprimerItem={(idx) => {
                setFormData({ ...formData, items_services: formData.items_services.filter((_, i) => i !== idx) });
              }}
            />
            </>
          )}

          {/* SECTION PRODUITS - masqué pour location */}
          {!isLocation && formData.afficher_produits && (
            <SectionProduits
              items={formData.items_produits}
              onItemsChange={(newItems) => setFormData({ ...formData, items_produits: newItems })}
            />
          )}

          {/* SECTION ÉQUIPEMENTS */}
          {formData.afficher_equipements && (
            <>
              {/* ✅ Vue éditeur (caché à l'impression) */}
              <div data-print-hide>
                <SectionEquipements
                  items={formData.items_equipements}
                  onItemsChange={(newItems) => setFormData({ ...formData, items_equipements: newItems })}
                />
              </div>

              {/* ✅ Vue tableau imprimable (visible UNIQUEMENT à l'impression) */}
              {formData.items_equipements.length > 0 && (
                <Card className="print-only" style={{ display: 'none' }}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Équipements loués</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #000' }}>
                          <th style={{ textAlign: 'left', padding: '6px 4px' }}>Description</th>
                          <th style={{ textAlign: 'left', padding: '6px 4px', width: '90px' }}>Du</th>
                          <th style={{ textAlign: 'left', padding: '6px 4px', width: '90px' }}>Au</th>
                          <th style={{ textAlign: 'right', padding: '6px 4px', width: '50px' }}>Qté</th>
                          <th style={{ textAlign: 'right', padding: '6px 4px', width: '90px' }}>Tarif</th>
                          <th style={{ textAlign: 'right', padding: '6px 4px', width: '90px' }}>Livraison</th>
                          <th style={{ textAlign: 'right', padding: '6px 4px', width: '100px' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items_equipements.map((it, i) => {
                          const dureeLabel = it.duree_type === 'semaine' ? 'sem' : it.duree_type === 'mois' ? 'mois' : 'jour';
                          const fmt = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('fr-CA', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
                          const totalLigne = ((parseFloat(it.prix_location) || 0) * (parseFloat(it.quantite) || 1))
                            + (parseFloat(it.prix_livraison) || 0)
                            + (parseFloat(it.extras_total) || 0);
                          return (
                            <tr key={i} style={{ borderBottom: '1px solid #ccc' }}>
                              <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>
                                <div style={{ fontWeight: 600 }}>{it.description || `Équipement #${i + 1}`}</div>
                                {(it.extras || []).filter(e => (e.description || e.nom) && (parseFloat(e.montant) || 0) > 0).map((ex, j) => (
                                  <div key={j} style={{ fontSize: '11px', color: '#555', marginLeft: 8 }}>
                                    + {ex.description || ex.nom || '(sans nom)'} : {(parseFloat(ex.montant) || 0).toFixed(2)} $
                                  </div>
                                ))}
                              </td>
                              <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>{fmt(it.date_prise)}</td>
                              <td style={{ padding: '6px 4px', verticalAlign: 'top' }}>{fmt(it.date_remise)}</td>
                              <td style={{ padding: '6px 4px', textAlign: 'right', verticalAlign: 'top' }}>{(parseFloat(it.quantite) || 1)}</td>
                              <td style={{ padding: '6px 4px', textAlign: 'right', verticalAlign: 'top' }}>
                                {(parseFloat(it.prix_location) || 0).toFixed(2)} $/{dureeLabel}
                              </td>
                              <td style={{ padding: '6px 4px', textAlign: 'right', verticalAlign: 'top' }}>
                                {(parseFloat(it.prix_livraison) || 0) > 0 ? (parseFloat(it.prix_livraison)).toFixed(2) + ' $' : '—'}
                              </td>
                              <td style={{ padding: '6px 4px', textAlign: 'right', verticalAlign: 'top', fontWeight: 600 }}>
                                {totalLigne.toFixed(2)} $
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* TOTAL GLOBAL */}
          {(formData.items_services.length + formData.items_produits.length + formData.items_equipements.length) > 0 && (
            <Card>
              <CardContent className="pt-6">
                <div className="max-w-md ml-auto">
                  {/* Toggle pour sous-total détaillé */}
                  <div className="flex items-center justify-end gap-2 mb-3" data-print-hide>
                    <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer" data-testid="toggle-sous-total-detaille">
                      <input 
                        type="checkbox" 
                        checked={formData.affichage?.sous_total_detaille || false}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          affichage: { ...prev.affichage, sous_total_detaille: e.target.checked }
                        }))}
                        className="rounded"
                      />
                      Détailler les lignes dans le sous-total
                    </label>
                  </div>
                  <table className="w-full">
                    <tbody className="text-sm">
                      {formData.affichage?.sous_total_detaille ? (
                        /* Mode détaillé: chaque ligne individuellement avec toggle */
                        <>
                          {(() => {
                            // ✅ Point 6 : pour les équipements, exploser en sous-lignes
                            // (location / livraison / extras nommés) afin que le client
                            // voie chaque composante du prix.
                            const allLines = [];
                            formData.items_services.forEach((it, i) =>
                              allLines.push({ ...it, _type: 'service', _idx: i, _key: `srv-${i}` })
                            );
                            formData.items_produits.forEach((it, i) =>
                              allLines.push({ ...it, _type: 'produit', _idx: i, _key: `prd-${i}` })
                            );
                            formData.items_equipements.forEach((it, i) => {
                              const baseDesc = it.description || `Équipement #${i + 1}`;
                              const locationMontant = (parseFloat(it.prix_location) || 0) * (parseFloat(it.quantite) || 1);
                              const livMontant = parseFloat(it.prix_livraison) || 0;
                              const extras = it.extras || [];

                              if (locationMontant > 0) {
                                const dureeLabel = it.duree_type === 'semaine' ? 'semaine' : it.duree_type === 'mois' ? 'mois' : 'jour';
                                const tarifLabel = `${baseDesc} — ${(parseFloat(it.prix_location) || 0).toFixed(2)} $/${dureeLabel} × ${parseFloat(it.quantite) || 1}`;
                                allLines.push({
                                  description: tarifLabel,
                                  montant: locationMontant,
                                  _type: 'equipement', _idx: i, _key: `eq-${i}-loc`,
                                  exclure_sous_total: it.exclure_sous_total,
                                });
                              }
                              if (livMontant > 0) {
                                const sensLabel = (it.livraison_sens || 'aller') === 'aller_retour' ? 'aller-retour' : 'aller seulement';
                                const livLabel = `Livraison ${sensLabel}${it.livraison_km ? ` (${it.livraison_km} km)` : ''}${it.livraison_endroit === 'precise' ? ' — endroit précis' : it.livraison_endroit === 'adresse' ? ' — devant adresse' : ''}`;
                                allLines.push({
                                  description: livLabel,
                                  montant: livMontant,
                                  _type: 'equipement', _idx: i, _key: `eq-${i}-liv`,
                                  exclure_sous_total: it.exclure_sous_total,
                                  _no_toggle: true,
                                });
                              }
                              extras.forEach((ex, eIdx) => {
                                const exMontant = parseFloat(ex.montant) || 0;
                                if (exMontant > 0) {
                                  allLines.push({
                                    description: ex.description || `Extra #${eIdx + 1}`,
                                    montant: exMontant,
                                    _type: 'equipement', _idx: i, _key: `eq-${i}-ex-${eIdx}`,
                                    exclure_sous_total: it.exclure_sous_total,
                                    _no_toggle: true,
                                  });
                                }
                              });
                            });
                            return allLines.map((item, gIdx) => {
                              const isExcluded = item.exclure_sous_total;
                              return (
                                <tr key={item._key} className={`border-b ${isExcluded ? 'opacity-40 line-through' : ''}`}>
                                  <td className="py-1.5">
                                    <div className="flex items-center gap-2">
                                      {item._no_toggle ? (
                                        <span className="w-4 h-4 inline-block" />
                                      ) : (
                                        <input
                                          type="checkbox"
                                          checked={!isExcluded}
                                          onChange={(e) => {
                                            const key = item._type === 'service' ? 'items_services' : item._type === 'produit' ? 'items_produits' : 'items_equipements';
                                            setFormData(prev => {
                                              const updated = [...prev[key]];
                                              updated[item._idx] = { ...updated[item._idx], exclure_sous_total: !e.target.checked };
                                              return { ...prev, [key]: updated };
                                            });
                                          }}
                                          className="rounded"
                                          data-testid={`toggle-st-line-${gIdx}`}
                                        />
                                      )}
                                      <span className={`text-xs ${item._key.includes('-liv') || item._key.includes('-ex-') ? 'text-slate-500 pl-2' : ''}`}>
                                        {item._key.includes('-liv') || item._key.includes('-ex-') ? '↳ ' : ''}
                                        {item.description || '—'}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="py-1.5 text-right font-medium">{(parseFloat(item.montant) || 0).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $</td>
                                </tr>
                              );
                            });
                          })()}
                        </>
                      ) : (
                        /* Mode classique: totaux par catégorie */
                        <>
                          {formData.items_services.length > 0 && (
                            <tr className="border-b"><td className="py-2">Total Services</td><td className="py-2 text-right font-medium">{totaux.totalServices.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $</td></tr>
                          )}
                          {formData.items_produits.length > 0 && (
                            <tr className="border-b"><td className="py-2">Total Produits</td><td className="py-2 text-right font-medium">{totaux.totalProduits.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $</td></tr>
                          )}
                          {formData.items_equipements.length > 0 && (
                            <tr className="border-b"><td className="py-2">Total Équipements</td><td className="py-2 text-right font-medium">{totaux.totalEquipements.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $</td></tr>
                          )}
                        </>
                      )}
                      <tr className="border-b"><td className="py-2 font-semibold">Sous-total</td><td className="py-2 text-right font-semibold">{totaux.sousTotal.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $</td></tr>

                      {/* Rabais */}
                      <tr className="bg-amber-50">
                        <td className="py-2 text-amber-700 font-medium">
                          <div className="flex items-center gap-2">
                            <span>Rabais</span>
                            <select className="text-xs border rounded px-1 py-0.5 bg-white" value={formData.rabais_type} onChange={(e) => setFormData({ ...formData, rabais_type: e.target.value })}>
                              <option value="dollar">$</option>
                              <option value="pourcentage">%</option>
                            </select>
                            <label className="flex items-center gap-1 text-xs text-gray-500 ml-2 cursor-pointer" data-testid="toggle-afficher-rabais">
                              <input type="checkbox" checked={formData.afficher_rabais} onChange={(e) => setFormData({ ...formData, afficher_rabais: e.target.checked })} className="rounded" />
                              Afficher
                            </label>
                          </div>
                        </td>
                        <td className="py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-amber-700">-</span>
                            <MoneyInput 
                              className="w-24 h-7 text-right text-sm" 
                              value={formData.rabais_valeur} 
                              onChange={(num) => setFormData({ ...formData, rabais_valeur: num })} 
                            />
                            <span className="text-amber-700 text-xs">{formData.rabais_type === 'pourcentage' ? '%' : '$'}</span>
                          </div>
                          {formData.rabais_valeur > 0 && <p className="text-xs text-amber-600 mt-1">-{totaux.rabais.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $</p>}
                        </td>
                      </tr>

                      <tr className="border-b"><td className="py-2 text-sm">TPS (5%)</td><td className="py-2 text-right">{totaux.tps.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $</td></tr>
                      <tr className="border-b"><td className="py-2 text-sm">TVQ (9.975%)</td><td className="py-2 text-right">{totaux.tvq.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $</td></tr>
                      <tr className="border-b-2 border-black"><td className="py-3 font-bold text-lg">TOTAL</td><td className="py-3 text-right font-bold text-lg">{totaux.total.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $</td></tr>
                      <tr className="border-b">
                        <td className="py-2">
                          <div className="flex items-center gap-2">
                            <span>Dépôt</span>
                            <select className="text-xs border rounded px-1 py-0.5 bg-white" value={formData.depot_type} onChange={(e) => setFormData({ ...formData, depot_type: e.target.value })}>
                              <option value="pourcentage">%</option>
                              <option value="dollar">$</option>
                            </select>
                          </div>
                        </td>
                        <td className="py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <MoneyInput 
                              className="w-20 h-7 text-right text-sm" 
                              value={formData.depot_valeur} 
                              onChange={(num) => setFormData({ ...formData, depot_valeur: num })} 
                            />
                            <span className="text-blue-700 text-xs">{formData.depot_type === 'pourcentage' ? '%' : '$'}</span>
                          </div>
                          <p className="text-xs text-blue-600 mt-1 font-medium">{totaux.depot.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $</p>
                        </td>
                      </tr>
                      <tr><td className="py-2 font-semibold text-green-600">Balance</td><td className="py-2 text-right font-semibold text-green-600">{totaux.balance.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $</td></tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}


          {/* TERMES ET CONDITIONS - masqué pour location */}
          {!isLocation && (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-bold">Termes et Conditions</CardTitle>
            </CardHeader>
            <CardContent>
              <EditorRiche
                value={formData.termes_conditions}
                onChange={(value) => setFormData({ ...formData, termes_conditions: value })}
                onChoixTemplate={() => handleChoixTemplate('termes')}
                onSauvegarderTemplate={() => handleSauvegarderTemplate('termes')}
                placeholder="Termes et conditions..."
              />
            </CardContent>
          </Card>
          )}

          {/* SIGNATURES */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-bold">Signatures</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Phrase d'acceptation */}
              <p className="text-sm text-gray-700 italic pl-3 py-2" data-testid="acceptance-phrase">
                {isLocation
                  ? "J'accepte les termes et conditions décrits dans le présent contrat de location et consent à louer les équipements correspondants."
                  : "J'accepte les termes et conditions décrits dans le présent document et consent à acheter les produits et/ou services correspondants."}
              </p>

              <div className="grid grid-cols-2 gap-6 pt-2">
                {/* Signature Client */}
                <div data-testid="client-signature-section">
                  <Label className="font-semibold">Signature Client *</Label>
                  {formData.signature_client_data ? (
                    <div className="mt-2">
                      <img src={formData.signature_client_data} alt="Signature client" className="border rounded h-24 bg-white" />
                      <p className="text-sm mt-1 font-medium">{formData.signature_client_nom}</p>
                      <p className="text-xs text-gray-500">Signé le {formData.date_signature_client || new Date().toLocaleDateString('fr-CA')}</p>
                      <Button size="sm" variant="outline" onClick={() => setShowSignatureClient(true)} className="mt-2" data-testid="modify-signature-client-btn">
                        Modifier
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <Button onClick={() => setShowSignatureClient(true)} data-testid="sign-client-btn">Signer</Button>
                    </div>
                  )}
                </div>

                {/* Signature Commerçant */}
                <div>
                  <Label className="font-semibold">Signature Commerçant</Label>
                  {signaturesActionnaires.length > 0 || signatureHistorique ? (
                    <div className="mt-2 space-y-3">
                      {signaturesActionnaires.length > 1 || formData.signature_commercant_idx === -1 ? (
                        <Select
                          value={formData.signature_commercant_idx !== undefined ? String(formData.signature_commercant_idx) : '0'}
                          onValueChange={(val) => setFormData({ ...formData, signature_commercant_idx: parseInt(val) })}
                        >
                          <SelectTrigger data-testid="select-merchant-signature">
                            <SelectValue placeholder="Choisir un signataire..." />
                          </SelectTrigger>
                          <SelectContent>
                            {formData.signature_commercant_idx === -1 && signatureHistorique && (
                              <SelectItem value="-1" data-testid="sig-option-historique">
                                ⚠️ {signatureHistorique.nom || 'Actionnaire modifié'} (version sauvegardée)
                              </SelectItem>
                            )}
                            {signaturesActionnaires.map((sig, idx) => (
                              <SelectItem key={sig.id || sig._id || idx} value={String(idx)}>{sig.nom}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : null}
                      {(() => {
                        const idx = formData.signature_commercant_idx;
                        // Cas "actionnaire modifié/supprimé" → afficher signature historique du doc
                        if (idx === -1 && signatureHistorique) {
                          return (
                            <div data-testid="sig-historique-preview">
                              <img src={signatureHistorique.data} alt="Signature historique" className="border rounded h-24 bg-white" />
                              <p className="text-sm mt-1 font-medium">{signatureHistorique.nom}</p>
                              <p className="text-xs text-amber-600 font-semibold" data-testid="sig-badge-historique">
                                ⚠️ Actionnaire modifié depuis la signature — version sauvegardée conservée.
                              </p>
                            </div>
                          );
                        }
                        const realIdx = idx === undefined || idx < 0 ? 0 : idx;
                        const sig = signaturesActionnaires[realIdx] || signaturesActionnaires[0];
                        if (!sig) return null;
                        return (
                          <div>
                            <img src={sig.signature} alt="Signature commerçant" className="border rounded h-24 bg-white" />
                            <p className="text-sm mt-1 font-medium">{sig.nom}</p>
                            <p className="text-xs text-gray-500">Signé le {new Date().toLocaleDateString('fr-CA')}</p>
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mt-2 p-3 bg-gray-50 rounded border border-dashed">
                      Aucune signature configurée. Allez dans <strong>Configuration → Infos Entreprise</strong> pour enregistrer vos signatures d'actionnaires.
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2">
                <div>
                  <Label>N° PO</Label>
                  <Input
                    placeholder="Optionnel"
                    value={formData.numero_po}
                    onChange={(e) => setFormData({ ...formData, numero_po: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* NOS PARTENAIRES */}
          {infosEntreprise?.logos_partenaires?.length > 0 && (
            <Card>
              <CardContent className="pt-6">
                <p className="text-center text-2xl font-bold mb-4">Nos partenaires</p>
                <div className="flex items-center justify-center gap-8 flex-wrap">
                  {infosEntreprise.logos_partenaires.map((logoUrl, idx) => (
                    <img key={logoUrl.id || logoUrl._id || idx} src={`${API_URL}${logoUrl}`} alt={`Partenaire ${idx + 1}`} className="h-14 w-auto object-contain" />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* DOCUMENTS CONTRACTUELS - après partenaires */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-bold flex items-center gap-2"><FileText className="w-5 h-5" /> Documents joints</CardTitle>
            </CardHeader>
            <CardContent>
              {documentsDisponibles.length === 0 ? (
                <p className="text-sm text-gray-500">Aucun document disponible. Configurez vos documents dans la page "Légal et Décharge".</p>
              ) : (
                <div className="space-y-4">
                  {documentsDisponibles.map((doc) => {
                    const isSelected = formData.documents_selectionnes.some(d => d.id === doc.id || d.code === doc.code);
                    return (
                      <div key={doc.id} className="rounded border border-gray-200" data-testid={`doc-item-${doc.id}`}>
                        <div className="flex items-center gap-3 px-3 py-2">
                          <Checkbox
                            id={`doc-${doc.id}`}
                            checked={isSelected}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setFormData({ ...formData, documents_selectionnes: [...formData.documents_selectionnes, { id: doc.id, nom: doc.nom, code: doc.code, contenu: doc.contenu, pdf_url: doc.pdf_url, pdf_filename: doc.pdf_filename, categorie: doc.code }] });
                              } else {
                                setFormData({ ...formData, documents_selectionnes: formData.documents_selectionnes.filter(d => d.id !== doc.id && d.code !== doc.code) });
                              }
                            }}
                          />
                          <span className="font-semibold text-sm text-gray-800">{doc.nom}</span>
                        </div>
                        {isSelected && doc.pdf_url ? (
                          <div className="px-4 py-2 border-t border-gray-100">
                            <a href={urlFichier(doc.pdf_url)} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-sm" data-testid={`pdf-link-${doc.code}`}>
                              Telecharger le document PDF
                            </a>
                          </div>
                        ) : isSelected && doc.contenu ? (
                          <div className="doc-contenu-print px-4 py-2 border-t border-gray-100 text-[11px] leading-relaxed text-gray-700 whitespace-pre-wrap text-justify" dangerouslySetInnerHTML={{ __html: doc.contenu }} />
                        ) : null}
                      </div>
                    );
                  })}
                  {formData.documents_selectionnes.length > 0 && (
                    <p className="text-xs text-gray-500">{formData.documents_selectionnes.length} document(s) sélectionné(s) — seront joints à la soumission</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
              </div>{/* fin space-y-6 inner */}
          </div>{/* fin soumission-print-area / paper */}
          </div>{/* fin scale wrapper */}
          </div>{/* fin flex center */}
        </div>{/* fin viewer area */}

          {/* BOUTONS ACTIONS */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t bg-white shrink-0">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={handleSubmit} disabled={loading || loadingData} className="bg-blue-600 hover:bg-blue-700">
              {loading ? 'Enregistrement...' : loadingData ? 'Chargement...' : 'Enregistrer les modifications'}
            </Button>
          </div>


        </DialogContent>

      {/* Modal Envoi Soumission */}
      <Dialog open={showEnvoiModal} onOpenChange={setShowEnvoiModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5" /> Envoyer la Soumission</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Sélection template */}
            {envoiTemplates.length > 0 && (
              <div>
                <Label>Choisir un modèle d'envoi</Label>
                <Select onValueChange={(val) => {
                  const t = envoiTemplates.find(t => t.id === val);
                  if (t) handleSelectEnvoiTemplate(t);
                }}>
                  <SelectTrigger data-testid="select-envoi-template"><SelectValue placeholder="Sélectionner un modèle..." /></SelectTrigger>
                  <SelectContent>
                    {envoiTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Email du client *</Label>
              <Input data-testid="envoi-email" value={envoiData.email_client} onChange={(e) => setEnvoiData({ ...envoiData, email_client: e.target.value })} placeholder="client@email.com" />
            </div>
            <div>
              <Label>Sujet *</Label>
              <Input data-testid="envoi-sujet" value={envoiData.sujet} onChange={(e) => setEnvoiData({ ...envoiData, sujet: e.target.value })} placeholder="Votre soumission..." />
            </div>
            <div>
              <Label>Message</Label>
              <div className="flex gap-0 mt-1">
                <button
                  type="button"
                  className={`px-3 py-1 rounded-t border text-xs font-medium ${!envoiData._preview ? 'bg-white border-b-white font-semibold' : 'bg-gray-100 text-gray-500'}`}
                  onClick={() => setEnvoiData(d => ({ ...d, _preview: false }))}
                >
                  Modifier
                </button>
                <button
                  type="button"
                  className={`px-3 py-1 rounded-t border text-xs font-medium ${envoiData._preview ? 'bg-white border-b-white font-semibold' : 'bg-gray-100 text-gray-500'}`}
                  onClick={() => { setEnvoiData(d => ({ ...d, _preview: true })); fetchPreview(envoiData.contenu); }}
                >
                  Aperçu
                </button>
              </div>
              {envoiData._preview ? (
                loadingPreview ? (
                  <div className="border rounded-b-md rounded-tr-md p-6 min-h-[200px] flex items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>
                ) : (
                  <EmailPreviewFrame html={previewHtml} minHeight={360} />
                )
              ) : (
                <Textarea data-testid="envoi-contenu" value={envoiData.contenu} onChange={(e) => setEnvoiData({ ...envoiData, contenu: e.target.value })} rows={10} placeholder="Contenu du courriel..." className="rounded-t-none" />
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowEnvoiModal(false)}>Annuler</Button>
              <Button onClick={handleSendEmail} disabled={sending} className="bg-green-600 hover:bg-green-700" data-testid="btn-send-email">
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Envoi...' : 'Envoyer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showChoixTemplate} onOpenChange={setShowChoixTemplate}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Choisir un ou plusieurs modèles</DialogTitle>
            <p className="text-sm text-slate-500">Sélectionnez plusieurs modèles pour les combiner dans votre soumission</p>
          </DialogHeader>



          <div className="space-y-4">
            {templatesDisponibles.length === 0 ? (
              <p className="text-gray-500 text-center py-12">
                Aucun modèle disponible pour cette section.
                <br />
                Créez votre premier modèle en cliquant sur "Sauvegarder comme modèle"!
              </p>
            ) : (
              templatesDisponibles.map((template) => (
                <Card key={template.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <h3 className="font-bold text-lg mb-4">{template.nom}</h3>
                    
                    {/* CONTENU COMPLET avec formatage HTML */}
                    <div 
                      className="prose max-w-none text-sm mb-4 p-4 bg-gray-50 rounded border"
                      dangerouslySetInnerHTML={{ __html: template.contenu }}
                      style={{ color: '#000', minHeight: '100px', maxHeight: '400px', overflowY: 'auto' }}
                    />
                    
                    <div className="flex gap-2 justify-end border-t pt-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setTemplateAModifier(template);
                          setShowModifierTemplate(true);
                        }}
                      >
                        ✏️ Modifier
                      </Button>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => appliquerTemplate(template)}
                      >
                        ✅ Sélectionner
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-600 hover:bg-red-50"
                        onClick={async () => {
                          if (!window.confirm(`Supprimer "${template.nom}"?`)) return;
                          
                          try {
                            const response = await axios.delete(`${API_URL}/api/messages-templates/${template.id}`);
                            if (response.status !== 200) throw new Error('Erreur');
                            
                            toast.success('Template supprimé!');
                            handleChoixTemplate(templateType);
                          } catch (error) {
                            toast.error('Erreur suppression');
                          }
                        }}
                      >
                        ❌ Supprimer
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
            <div className="flex justify-end pt-4 border-t">
              <Button onClick={() => setShowChoixTemplate(false)} data-testid="template-close-btn">
                Terminé
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Sauvegarder Template */}
      <Dialog open={showSauvegarderTemplate} onOpenChange={setShowSauvegarderTemplate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sauvegarder comme Modèle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom du Modèle</Label>
              <Input
                id="nom-template"
                placeholder="Ex: Description Peinture Intérieure Standard"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    sauvegarderNouveauTemplate(e.target.value);
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSauvegarderTemplate(false)}>
                Annuler
              </Button>
              <Button
                onClick={() => {
                  const nom = document.getElementById('nom-template').value;
                  if (nom) sauvegarderNouveauTemplate(nom);
                  else toast.error('Entrez un nom');
                }}
              >
                Sauvegarder
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Prévisualisation Document */}
      <Dialog open={!!documentPreview} onOpenChange={() => setDocumentPreview(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {documentPreview?.nom}
            </DialogTitle>
          </DialogHeader>
          <div
            className="prose max-w-none text-sm p-6 bg-white rounded border"
            dangerouslySetInnerHTML={{ __html: documentPreview?.contenu || '' }}
            style={{ color: '#000', minHeight: '200px' }}
          />
        </DialogContent>
      </Dialog>

      {/* Modal Signature Client */}
      <Dialog open={showSignatureClient} onOpenChange={setShowSignatureClient}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Signature du Client</DialogTitle>
          </DialogHeader>
          <SignatureCanvas
            onSave={handleSignatureClient}
            onCancel={() => setShowSignatureClient(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Modal Sauvegarder Template Nom du Modèle */}
      <Dialog open={showSauvegarderTemplate} onOpenChange={setShowSauvegarderTemplate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sauvegarder en Modèle</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="nom-template">No</Label>
              <Input
                id="nom-template"
                placeholder="Ex: Description Peinture Intérieure Standard"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    sauvegarderNouveauTemplate(e.target.value);
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSauvegarderTemplate(false)}>
                Annuler
              </Button>
              <Button
                onClick={() => {
                  const nom = document.getElementById('nom-template').value;
                  if (nom) sauvegarderNouveauTemplate(nom);
                  else toast.error('Entrez un nom');
                }}
              >
                Sauvegarder
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Prévisualisation Document */}
      <Dialog open={!!documentPreview} onOpenChange={() => setDocumentPreview(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {documentPreview?.nom}
            </DialogTitle>
          </DialogHeader>
          <div
            className="prose max-w-none text-sm p-6 bg-white rounded border"
            dangerouslySetInnerHTML={{ __html: documentPreview?.contenu || '' }}
            style={{ color: '#000', minHeight: '200px' }}
          />
        </DialogContent>
      </Dialog>

      {/* Modal Signature Client */}
      <Dialog open={showSignatureClient} onOpenChange={setShowSignatureClient}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Signature du Client</DialogTitle>
          </DialogHeader>
          <SignatureCanvas
            onSave={handleSignatureClient}
            onCancel={() => setShowSignatureClient(false)}
          />
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
